import React, { useState } from "react";
import {
  Container,
  TextField,
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  InputAdornment,
  Alert,
  Link as MuiLink,
  IconButton,
} from "@mui/material";
import { Link } from "react-router-dom";
import { Visibility, VisibilityOff, Lock, Email } from "@mui/icons-material";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";

import footer from "../utils/footer";
import colors from "../config/colors";
import AppButton from "../utils/Appbutton";
import { login } from "../services/auth";
import branding from "../config/branding";

// Validation schema
const validationSchema = Yup.object({
  email: Yup.string().email("Email inválido").required("Email é obrigatório"),
  password: Yup.string()
    .min(6, "Senha deve ter no mínimo 6 caracteres")
    .required("Senha é obrigatória"),
});

const LoginScreen = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const validateField = async (name, value) => {
    try {
      await validationSchema.validateAt(name, { [name]: value });
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [name]: err.message }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name, formData[name]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");

    try {
      await validationSchema.validate(formData, {
        abortEarly: false,
      });

      setLoading(true);

      const res = await login(formData);

      if (res) {
        // REDIRECT BY USER TYPE
        if (res.user_type === "client") {
          navigate("/clientDashboard");
        } else if (res.user_type === "company") {
          navigate("/dashboard");
        }
      }

      setLoading(false);

      // RESET FORM
      setFormData({
        email: "",
        password: "",
      });
    } catch (err) {
      if (err.inner) {
        const newErrors = {};

        err.inner.forEach((e) => {
          newErrors[e.path] = e.message;
        });

        setErrors(newErrors);
      }

      const apiMessage = err?.response?.data?.message;
      setError(apiMessage || "Por favor, verifique os dados informados");

      setLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card
          elevation={6}
          sx={{
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              background: "linear-gradient(135deg, #9c27b0 0%, #757575 100%)",
              color: "white",
              padding: 3,
              textAlign: "center",
            }}
          >
            <Box
              component="img"
              src={branding.logo}
              alt="Logo"
              sx={{
                height: 280,
                width: 280,
                borderRadius: "15%",
              }}
            />
          </Box>

          <CardContent sx={{ padding: 4 }}>
            {error && (
              <Alert severity="error" sx={{ marginBottom: 2 }}>
                {error}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Stack spacing={3} sx={{ width: "100%" }}>
                {/* Email Field */}
                <TextField
                  fullWidth
                  id="email"
                  name="email"
                  label="Email"
                  type="email"
                  placeholder="seu.email@exemplo.com"
                  variant="filled"
                  value={formData.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email sx={{ color: colors.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#fafafa",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        backgroundColor: "#f5f5f5",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "#fff",
                      },
                    },
                  }}
                />

                {/* Password Field */}
                <Box sx={{ position: "relative" }}>
                  <TextField
                    fullWidth
                    id="password"
                    name="password"
                    label="Senha"
                    type={showPassword ? "text" : "password"}
                    placeholder="Digite sua senha"
                    variant="filled"
                    value={formData.password}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Lock sx={{ color: "#9c27b0" }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#fafafa",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: "#f5f5f5",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#fff",
                        },
                      },
                    }}
                  />
                  <IconButton
                    onClick={handleClickShowPassword}
                    sx={{
                      position: "absolute",
                      right: 30,
                      top: "50%",
                      transform: "translateY(-50%)",
                      padding: 0.5,
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </Box>

                {/* Login Button */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: 2,
                  }}
                >
                  <AppButton
                    type="submit"
                    valueText="Entrar"
                    loading={loading}
                  />
                </Box>

                {/* Forgot Password Link */}
                <Box sx={{ textAlign: "center", marginTop: 1 }}>
                  <MuiLink
                    component={Link}
                    to="/forgot-password"
                    variant="body2"
                    sx={{
                      color: "#9c27b0",
                      textDecoration: "none",
                      fontSize: "0.95rem",
                      fontWeight: 500,
                      "&:hover": {
                        textDecoration: "underline",
                        color: "#6a1b9a",
                      },
                    }}
                  >
                    Esqueceu a senha?
                  </MuiLink>
                </Box>

                {/* Sign Up Link */}
                <Box
                  sx={{
                    textAlign: "center",
                    marginTop: 2,
                    paddingTop: 2,
                    borderTop: "1px solid #e0e0e0",
                  }}
                >
                  <Typography variant="body2" sx={{ color: "#424242" }}>
                    Não tem uma conta?{" "}
                    <Link
                      to="/register"
                      style={{
                        color: "#9c27b0",
                        fontWeight: "bold",
                        textDecoration: "none",
                      }}
                    >
                      Cadastre-se aqui
                    </Link>
                  </Typography>
                </Box>
              </Stack>
            </form>
          </CardContent>
        </Card>
        {/* Footer */}
        {footer()}
      </Container>
    </Box>
  );
};

export default LoginScreen;
